<template>
  <div ref="containerRef">
    <span
      v-for="(label, index) in labels"
      :key="index"
      :ref="(el) => setRef(el as HTMLElement, index)"
      class="text-label-medium text-on-surface absolute top-0 whitespace-nowrap"
      :style="{
        left: label.left,
        transform: `translateX(calc(-50% + ${offsets[index] || 0}px))`,
      }"
    >
      {{ label.texts.join(' & ') }}
    </span>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'

  interface Props {
    labels: {
      left: string
      texts: string[]
    }[]
    edgeOverflow?: number
  }

  const props = defineProps<Props>()

  // 1. Ref for the parent container
  const containerRef = ref<HTMLElement | null>(null)

  const labelRefs = ref<Map<number, HTMLElement>>(new Map())
  const setRef = (el: HTMLElement, index: number) => {
    if (el) labelRefs.value.set(index, el as HTMLElement)
  }

  const offsets = ref<number[]>([])
  const GAP = 12
  const EDGE_OVERFLOW = props.edgeOverflow ?? 0 // Allowed overflow outside the container edges

  const calculateCollisions = async () => {
    offsets.value = props.labels.map(() => 0)
    await nextTick()

    // Ensure both labels and container are rendered
    if (labelRefs.value.size === 0 || !containerRef.value) return

    // 2. Measure the container's absolute boundaries
    const containerRect = containerRef.value.getBoundingClientRect()
    const minBoundaryX = containerRect.left - EDGE_OVERFLOW
    const maxBoundaryX = containerRect.right + EDGE_OVERFLOW

    const items = props.labels
      .map((_, index) => {
        const el = labelRefs.value.get(index)
        if (!el) return null

        const rect = el.getBoundingClientRect()
        return {
          index,
          width: rect.width,
          origCenter: rect.left + rect.width / 2,
          currentCenter: rect.left + rect.width / 2,
        }
      })
      .filter(Boolean) as {
      index: number
      width: number
      origCenter: number
      currentCenter: number
    }[]

    items.sort((a, b) => a.origCenter - b.origCenter)

    const ITERATIONS = 20

    for (let iter = 0; iter < ITERATIONS; iter++) {
      const shifts = items.map(() => 0)

      // A. Check for Sibling Overlaps
      for (let i = 0; i < items.length - 1; i++) {
        const leftItem = items[i]
        const rightItem = items[i + 1]

        const distance = rightItem.currentCenter - leftItem.currentCenter
        const minDistance = (leftItem.width + rightItem.width) / 2 + GAP

        if (distance < minDistance) {
          const overlap = minDistance - distance
          shifts[i] -= overlap / 2
          shifts[i + 1] += overlap / 2
        }
      }

      // B. Apply Sibling Shifts
      items.forEach((item, i) => {
        item.currentCenter += shifts[i]
      })

      // C. Enforce Container Boundaries (Rigid Wall)
      items.forEach((item) => {
        const itemLeftEdge = item.currentCenter - item.width / 2
        const itemRightEdge = item.currentCenter + item.width / 2

        if (itemLeftEdge < minBoundaryX) {
          // Pushed too far left! Force it back to the left boundary
          item.currentCenter += minBoundaryX - itemLeftEdge
        } else if (itemRightEdge > maxBoundaryX) {
          // Pushed too far right! Force it back to the right boundary
          item.currentCenter -= itemRightEdge - maxBoundaryX
        }
      })
    }

    const newOffsets = [...offsets.value]
    items.forEach((item) => {
      newOffsets[item.index] = item.currentCenter - item.origCenter
    })

    offsets.value = newOffsets
  }

  onMounted(() => {
    calculateCollisions()
    window.addEventListener('resize', calculateCollisions)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', calculateCollisions)
  })

  watch(() => props.labels, calculateCollisions, { deep: true })
</script>
